import React from 'react';
import PropTypes from 'prop-types';

import * as Sentry from '@sentry/browser';

import { ToastContext, CommentCard } from '@weezevent/nacre';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import CommentModel from '../../../../../models/attendeeComment';
import useAppContext from '../../../../../context/useAppContext';
import LoadingCard from '../../../../../components/loadingCard';
import { useSegment } from '../../../../../components/tracking/trackingProvider';

export function Comment({ attendeeId }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const locale = i18n.language;
    const [{ organization, event }] = useAppContext();
    const toast = React.useContext(ToastContext);

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [comments, loading, sync] = CommentModel.useApiModel({//eslint-disable-line
        eventId: event.id,
        organizationId: organization.id,
        attendeeId,
        onError: () => {
            toast.error(t(`ticket.manager.attendees.detail.comment.retrievalError`));
        }
    });

    const commentItems = React.useMemo(() => {
        if (!comments) {
            return [];
        }

        let allComments = comments.map(comment => {
            return {
                id: comment.uuid,
                date: comment.created,
                text: comment.comment,
                user: comment.resolved_user_name
            };
        });

        return allComments;
    }, [comments]);

    const onSubmit = React.useCallback(
        value => {
            setIsSubmitting(true);
            let comment = new CommentModel({ comment: value });

            comment
                .save({ organizationId: organization.id, attendeeId, eventId: event.id })
                .then(() => {
                    toast.success(t(`ticket.manager.attendees.detail.comment.saveSucess`));
                    sync();
                })
                .catch(e => {
                    console.error(e);
                    Sentry.captureException(e);
                    toast.error(t(`ticket.manager.attendees.detail.comment.saveError`));
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
            track('ACTION > ATTENDEE COMMENT', { 'Page Title': 'ATTENDEE PROFILE' });
        },
        [attendeeId, organization, event, sync]
    );

    const onEdit = React.useCallback(
        (id, value) => {
            setIsSubmitting(true);
            let comment = comments.find(c => c.uuid === id);

            if (!comment) {
                console.error('comment to edit not found');
                toast.error(t(`ticket.manager.attendees.detail.comment.editError`));
                return;
            }

            comment.comment = value;
            comment
                .save({ organizationId: organization.id, attendeeId, eventId: event.id })
                .then(() => {
                    toast.success(t(`ticket.manager.attendees.detail.comment.editSucess`));
                    sync();
                })
                .catch(e => {
                    console.error(e);
                    Sentry.captureException(e);
                    toast.error(t(`ticket.manager.attendees.detail.comment.editError`));
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [attendeeId, organization, event, sync]
    );

    const onDelete = React.useCallback(
        id => {
            setIsSubmitting(true);
            let comment = comments.filter(c => c.uuid === id);

            if (!comment) {
                console.error('comment to delete not found');
                toast.error(t(`ticket.manager.attendees.detail.comment.deleteError`));
                return;
            }

            CommentModel.destroy(comment, { organizationId: organization.id, attendeeId, eventId: event.id })
                .then(() => {
                    toast.success(t(`ticket.manager.attendees.detail.comment.deleteSucess`));
                    sync();
                })
                .catch(e => {
                    console.error(e);
                    Sentry.captureException(e);
                    toast.error(t(`ticket.manager.attendees.detail.comment.deleteError`));
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [attendeeId, organization, event, sync]
    );

    return (
        <CommentCard
            items={commentItems}
            onSubmit={onSubmit}
            onDelete={onDelete}
            onEdit={onEdit}
            loading={isSubmitting || loading ? <LoadingCard loading={true} /> : false}
            tradDelete={t('ticket.common.action.delete')}
            tradEdit={t('ticket.common.action.edit')}
            tradEmpty={t('ticket.manager.attendees.detail.comment.noComments')}
            tradViewAll={t('ticket.manager.attendees.detail.comment.viewAll')}
            tradPlaceholder={t('ticket.manager.attendees.detail.comment.addCommentPlaceholder')}
            tradTitle={t('ticket.manager.attendees.detail.comment.title')}
            tradViewMore={t('ticket.manager.attendees.detail.comment.viewMore')}
            locale={locale}
        />
    );
}

Comment.propTypes = {
    attendeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};
